import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { useUnloggedCount } from '../contexts/UnloggedCountContext';
import './TopNavigation.css';
import ChecklistLogger from './ChecklistLogger';
import { Icon } from './Icon.js';
import NotificationDropdown from './NotificationDropdown';
import ExpandingSearch from './ExpandingSearch';
import FeedbackBox from './FeedbackBox';

const UserSearchModal = ({ open, onClose }) => {
  const [allUsers, setAllUsers] = React.useState([]);
  const [userSearch, setUserSearch] = React.useState('');
  const [currentUserId, setCurrentUserId] = React.useState(null);
  const navigate = useNavigate();
  
  // Add click outside functionality
  useEffect(() => {
    if (!open) return;
    
    const handleClickOutside = (event) => {
      const modal = document.querySelector('.user-search-modal');
      const searchButton = document.querySelector('.nav-icon[title="Search"]');
      
      if (modal && !modal.contains(event.target) && 
          searchButton && !searchButton.contains(event.target)) {
        onClose();
      }
    };
    
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);
  
  useEffect(() => {
    if (!open) return;
    const fetchAllUsers = async () => {
      // Fetch current user id first - use getSession() to avoid network requests
      const { data: { session } = {} } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id || null;
      setCurrentUserId(currentUserId);
      
      // Then fetch all users with deduplication
      const allUsersCacheKey = getCacheKey('fetchAllUserProfiles', 'all');
      const profiles = await dedupeRequest(allUsersCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id,profile_picture_url,username');
        if (error) throw error;
        return data;
      });
      
      // Filter out current user immediately
      const filteredProfiles = (profiles || []).filter(u => u.user_id !== currentUserId);
      setAllUsers(filteredProfiles);
    };
    fetchAllUsers();
  }, [open]);
  
  const filteredAllUsers = userSearch
    ? allUsers.filter(u => (u.username || '').toLowerCase().includes(userSearch.toLowerCase()))
    : allUsers;
  if (!open) return null;
  return (
    <div className="user-search-modal-bg" onClick={onClose}>
      <div className="user-search-modal" onClick={e => e.stopPropagation()}>
        <div className="user-search-header">
          <div>
            <div className="user-search-title">Search Users</div>
          </div>
          <button 
            className="close-search-btn"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <input
          className="user-search-input"
          placeholder="Search users..."
          value={userSearch}
          onChange={e => setUserSearch(e.target.value)}
        />
        <div className="user-search-list">
          {filteredAllUsers.length === 0 ? (
            <div className="user-search-empty">No users found.</div>
          ) : (
            filteredAllUsers.map(u => (
              <div
                key={u.user_id}
                className="user-search-user"
                onClick={() => { navigate(`/user/${u.user_id}`); onClose(); }}
              >
                <div className="user-search-avatar">
                  {u.profile_picture_url ? (
                    <img src={u.profile_picture_url} alt="avatar" />
                  ) : (
                    <span className="user-search-avatar-fallback">
                      {(u.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="user-search-username">{u.username}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const TopNavigation = ({ activePage = 'home' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Add state for checklist panel
  const [isChecklistOpen, setChecklistOpen] = useState(false);
  const { count: unloggedCount, refresh: refreshUnloggedCount } = useUnloggedCount();
  const [showUserSearch, setShowUserSearch] = useState(false);
  
  // Add state for notifications
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const cached = sessionStorage.getItem('current_user_profile');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Ensure avatar_url exists if profilePictureUrl exists
        if (!parsed.avatar_url && parsed.profilePictureUrl) {
          parsed.avatar_url = parsed.profilePictureUrl;
        }
        return parsed;
      }
    } catch (e) {
      // Ignore error
    }
    return null;
  });
  
  // Add state for feedback modal
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  
  // Add state for scroll effect
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Determine active page from location
  const getActivePage = () => {
    const path = location.pathname;
    if (path === '/home' || path === '/dashboard') return 'home';
    if (path.startsWith('/protocols/catalogue')) return 'catalogue';
    if (path === '/insights') return 'insights';
    if (path === '/messages') return 'messages';
    if (path.startsWith('/user')) return 'user';
    if (path === '/checklist') return 'checklist';
    if (path === '/feedback') return 'feedback';
    return activePage;
  };
  
  const currentPage = getActivePage();


  // Fetch user profile
  const fetchUserProfile = async (forceRefresh = false) => {
    try {
      // Use getSession() to avoid network requests
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setUserProfile(null);
        return;
      }

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedProfile = sessionStorage.getItem(`userProfile_${user.id}`);
        if (cachedProfile) {
          try {
            const parsedProfile = JSON.parse(cachedProfile);
            // Ensure avatar_url is populated for display
            if (!parsedProfile.avatar_url && parsedProfile.profilePictureUrl) {
              parsedProfile.avatar_url = parsedProfile.profilePictureUrl;
            }
            
            // Check if cached profile has avatar_url
            if (parsedProfile.avatar_url || parsedProfile.profile_picture_url) {
              setUserProfile(parsedProfile);
              // Update current_user_profile to keep it fresh
              sessionStorage.setItem('current_user_profile', JSON.stringify(parsedProfile));
              return;
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
      }

      // Fetch from database - use deduplication to prevent duplicate requests
      // Use same cache key as Home.js to share the request
      const cacheKey = getCacheKey('fetchUserProfile', user.id);
      const profileData = await dedupeRequest(cacheKey, async () => {
        const { data } = await supabase
          .from('user_profiles')
          .select('username, profile_picture_url, gender')
          .eq('user_id', user.id)
          .maybeSingle();
        return data;
      }, { keepAliveMs: 2000, caller: 'TopNavigation' });

      if (profileData) {
        const profile = {
          id: user.id,
          username: profileData.username || 'User',
          profile_picture_url: profileData.profile_picture_url,
          avatar_url: profileData.profile_picture_url, // Add alias for consistency
          profilePictureUrl: profileData.profile_picture_url || '', // Add camelCase for useUserProfile hook
          gender: profileData.gender || null // Include gender for BiomarkerSelector
        };
        setUserProfile(profile);
        // Store with same structure as Home.js for consistency, but ensure avatar_url is present
        const cacheData = {
          id: user.id,
          username: profileData.username || '',
          profilePictureUrl: profileData.profile_picture_url || '',
          avatar_url: profileData.profile_picture_url || '', 
          gender: profileData.gender || null
        };
        sessionStorage.setItem(`userProfile_${user.id}`, JSON.stringify(cacheData));
        sessionStorage.setItem('current_user_profile', JSON.stringify(cacheData));
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    fetchUserProfile(false); // Use cache first, then refresh if needed
    fetchNotificationAndMessageCounts();
    
    // Listen for profile updates to keep cache fresh
    const channel = supabase
      .channel('profile_updates_nav')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles'
      }, (payload) => {
        // Refresh profile data
        fetchUserProfile();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Refresh counts when component becomes visible (e.g., returning from messages page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUserProfile(); // Also refresh profile picture
        fetchNotificationAndMessageCounts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Add scroll listener for navbar depth effect
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Check initial scroll position
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isNotificationOpen && !event.target.closest('.nav-icon') && !event.target.closest('.notification-dropdown')) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen]);

  // Fetch both notification and message counts in one place (two parallel requests).
  // - Total unread: for bell badge (we show total minus messages).
  // - Message unread: for messages icon badge.
  const fetchNotificationAndMessageCounts = async () => {
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setNotificationCount(0);
        setUnreadMessageCount(0);
        return;
      }

      const countCacheKey = getCacheKey('NotificationService.getUnreadCount', user.id);
      const messageCacheKey = getCacheKey('fetchUnreadMessageCount', user.id);

      const [totalCount, messageCount] = await Promise.all([
        dedupeRequest(countCacheKey, async () => {
          const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('is_read', false);
          if (error) throw error;
          return count || 0;
        }),
        dedupeRequest(messageCacheKey, async () => {
          const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('type', 'message')
            .eq('is_read', false);
          if (error) throw error;
          return count || 0;
        }),
      ]);

      setUnreadMessageCount(messageCount);
      setNotificationCount(totalCount - messageCount);
    } catch (err) {
      setNotificationCount(0);
      setUnreadMessageCount(0);
    }
  };

  // Single-purpose fetches for when only one count changes (e.g. after opening messages).
  const fetchUnreadMessageCount = async () => {
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setUnreadMessageCount(0);
        return;
      }
      const cacheKey = getCacheKey('fetchUnreadMessageCount', user.id);
      const messageCount = await dedupeRequest(cacheKey, async () => {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('type', 'message')
          .eq('is_read', false);
        if (error) throw error;
        return count || 0;
      });
      setUnreadMessageCount(messageCount);
    } catch (err) {
      setUnreadMessageCount(0);
    }
  };

  // When checklist closes, refresh count
  const handleChecklistClose = () => {
    setChecklistOpen(false);
    refreshUnloggedCount();
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Close all dropdowns
    setIsNotificationOpen(false);
    setShowUserSearch(false);
    
    // On desktop only, close modals (on mobile these are pages, not modals)
    if (window.innerWidth > 480) {
      setChecklistOpen(false);
      setIsFeedbackOpen(false);
    }
    
    navigate('/home');
  };

  const toggleNotifications = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsNotificationOpen(!isNotificationOpen);
    if (!isNotificationOpen) {
      fetchNotificationAndMessageCounts();
    }
  };

  const handleNavClick = (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Close all dropdowns
    setIsNotificationOpen(false);
    setShowUserSearch(false);
    
    // On desktop only, close modals (on mobile these are pages, not modals)
    if (window.innerWidth > 480) {
      setChecklistOpen(false);
      setIsFeedbackOpen(false);
    }
    
    navigate(path);
  };

  return (
    <nav className={`top-navigation ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        {/* LEFT: Logo and Search */}
        <div className="nav-left">
          <button 
            className="nav-logo" 
            onClick={handleLogoClick}
            aria-label="Cetarus Home"
          >
            <svg
              version="1.1"
              id="svg1"
              width="450"
              height="250"
              viewBox="0 0 400 300"
              xmlns="http://www.w3.org/2000/svg"
              className="nav-logo-svg"
            >
              <defs id="defs1" />
              <g id="g1">
                <path
                  style={{ fill: '#000000', fillOpacity: 1 }}
                  d="m 36.324107,269.8424 c -6.327,-2.00097 -9.95387,-4.21514 -13.47665,-8.22737 -4.5408,-5.17168 -6.02335,-9.26867 -6.02335,-16.64535 0,-14.52134 10.98445,-25.4399 25.80718,-25.65238 7.71011,-0.11053 13.58888,1.68364 17.90151,5.46343 l 3.79131,3.32288 -3.71957,3.77171 -3.71957,3.7717 -2.28043,-2.13629 c -6.97288,-6.53214 -18.08848,-5.24252 -23.53043,2.72998 -3.10435,4.54791 -3.10278,12.4393 0.003,17.04856 4.99831,7.41699 13.11502,9.15267 21.33002,4.56122 l 4.76545,-2.66346 3.32557,3.22326 c 1.82906,1.77279 3.31595,3.47249 3.30421,3.7771 -0.0539,1.39801 -8.02024,5.9887 -12.91458,7.44216 -6.36767,1.891 -9.13014,1.93137 -14.56407,0.21285 z m 255.500003,0.50962 c -7.45121,-1.60362 -12.72559,-5.81647 -15.45227,-12.34233 -1.1914,-2.85142 -1.54773,-7.62548 -1.54773,-20.73635 v -17.03212 l 5.75,-0.002 5.75,-0.002 -0.3243,11.75197 c -0.44922,16.27866 0.34584,21.68491 3.74918,25.49391 2.29669,2.57045 3.50541,3.09138 7.17295,3.09138 10.22129,0 11.90217,-3.50959 11.90217,-24.85112 v -15.58202 l 5.71912,0.29991 5.71912,0.2999 -0.0433,16 c -0.0447,16.51186 -0.886,21.45223 -4.54073,26.66417 -3.71783,5.30195 -15.52275,8.73968 -23.85421,6.94663 z m 48.23015,-0.60187 c -5.50871,-1.5451 -10.80639,-3.95952 -11.5234,-5.25182 -0.23104,-0.41641 0.43557,-2.67677 1.48137,-5.02302 l 1.90144,-4.26591 4.20522,2.4595 c 8.74506,5.11471 20.67798,5.38422 22.22753,0.50203 1.20386,-3.79301 -2.07789,-6.12117 -12.4767,-8.85128 -12.53052,-3.28979 -16.88472,-7.78575 -15.74798,-16.26073 1.10763,-8.25804 7.67626,-13.02049 18.83173,-13.65355 6.77543,-0.3845 17.12588,1.82178 18.42759,3.92799 0.3623,0.58622 0.0952,3.07852 -0.59351,5.53844 -0.97989,3.49982 -1.60145,4.32324 -2.85784,3.78594 -9.54281,-4.081 -17.68903,-4.6393 -20.60928,-1.41246 -1.42633,1.57608 -1.59416,2.48578 -0.7859,4.25973 0.79922,1.75409 3.36849,2.97212 11.55437,5.47763 8.3933,2.56901 11.0544,3.8492 13.13091,6.317 3.31504,3.9397 3.56671,10.85077 0.57389,15.75961 -3.91809,6.42647 -17.23236,9.63794 -27.73944,6.6909 z M 72.209037,268.6805 c 0.33829,-0.88157 0.61507,-12.14105 0.61507,-25.02107 v -23.41821 h 18.999998 19.000005 l 10e-4,4.75 0.001,4.75 -13.001425,-0.32947 -13.000578,-0.32947 v 5.57947 5.57947 h 11.499998 11.500005 l 10e-4,4.75 0.001,4.75 -11.440145,-0.33455 -11.440128,-0.33455 -0.64789,3.45356 c -0.35634,1.89946 -0.49637,4.525 -0.31118,5.83455 0.33544,2.37193 0.39001,2.38144 14.336718,2.50023 l 14.000005,0.11924 v 4.38076 4.38076 l -20.365075,0.27107 c -18.340008,0.24412 -20.303918,0.11169 -19.749998,-1.33179 z m 60.385783,-18.60093 -0.27071,-20.16166 -7.75,0.22695 -7.75,0.22695 v -5.06529 -5.0653 h 22 22 v 5 5 h -8 -8 v 20 20 h -5.97929 -5.9793 z m 26.15197,17.91165 c 1.20858,-3.11425 17.39164,-39.38801 19.64893,-44.04239 l 1.83924,-3.79238 5.98049,0.29238 5.9805,0.29239 6.84096,15.5 c 3.76253,8.525 8.62887,19.23785 10.81408,23.80634 2.18522,4.56849 3.97312,8.73099 3.97312,9.25 0,0.51901 -2.73871,0.94366 -6.08603,0.94366 h -6.08603 l -2.11244,-5 -2.11244,-5 h -11.7011 -11.7011 l -1.79797,5 -1.79798,5 h -6.2777 c -6.10227,0 -6.25331,-0.0629 -5.40453,-2.25 z m 34.07732,-17.68605 c 0,-1.70625 -6.30006,-17.06395 -7,-17.06395 -0.69994,0 -7,15.3577 -7,17.06395 0,0.51483 3.15,0.93605 7,0.93605 3.85,0 7,-0.42122 7,-0.93605 z m 28,-5.06395 v -25 l 14.75,0.022 c 12.23903,0.0182 15.4507,0.34017 18.86609,1.89116 11.34542,5.15216 13.70823,19.29905 4.80076,28.74372 -1.87927,1.99261 -3.41685,3.80966 -3.41685,4.03791 0,0.22824 1.81069,3.08134 4.02375,6.34022 6.19713,9.12568 6.22127,8.96503 -1.34672,8.96503 h -6.53694 l -4.14009,-7.50004 -4.14009,-7.5 h -5.42996 -5.42995 v 7.5 7.5 h -6 -6 z m 28.97456,-0.97456 c 7.14796,-7.14796 0.75746,-15.5241 -11.31039,-14.82472 l -5.16417,0.29928 -0.29044,8.25 -0.29044,8.25 h 7.54044 c 6.17473,0 7.89807,-0.35763 9.515,-1.97456 z m -89.70462,-69.52678 c -4.25178,-2.12708 -8.81821,-5.44648 -11.63435,-8.45716 -5.52049,-5.90185 -5.89233,-7.54285 -1.51059,-6.6665 1.71875,0.34375 6.81098,0.625 11.31607,0.625 h 8.19107 l 4.18393,8.25157 c 2.30116,4.53836 4.18393,8.58836 4.18393,9 0,2.0263 -8.26429,0.48178 -14.73006,-2.75291 z m 45.83551,-2.97307 c -16.63086,-4.02108 -32.79578,-16.81084 -41.10892,-32.52559 l -4.49653,-8.5 -7,-0.653 c -12.05307,-1.12438 -20.92832,-3.67791 -27.46303,-7.9015 -16.0391,-10.36657 -21.17255,-11.8546 -45.036973,-13.05485 -26.35497,-1.32552 -47.36244,-3.50959 -50.78617,-5.28007 -6.60701,-3.41662 -6.73956,-15.303122 -0.21609,-19.377102 2.62531,-1.63953 15.52887,-2.91433 42.50226,-4.19899 12.1,-0.57628 24.166748,-1.31604 26.814998,-1.64392 2.648255,-0.32787 34.598255,-0.036 71.000005,0.64869 97.4079,1.83206 99.60639,1.45834 136.90518,-23.27278 9.22026,-6.11353 16.59833,-11.28131 16.3957,-11.48394 -0.20263,-0.20263 -2.7866,0.55068 -5.74215,1.67402 -2.95555,1.12334 -5.48623,2.04266 -5.62373,2.04294 -0.1375,2.7e-4 -0.25,-3.3271 -0.25,-7.39416 0,-11.00385 2.94321,-19.51558 8.8768,-25.67157 6.34698,-6.5848802 7.58027,-6.4798502 6.75112,0.57498 -0.75283,6.40537 0.72667,10.8532 5.36878,16.14027 3.13225,3.56744 3.83912,7.11516 1.81997,9.13431 -1.98082,1.98082 -0.37339,2.30996 5.10517,1.04535 6.80005,-1.56964 12.40111,-0.13265 20.82656,5.3432 7.45509,4.8452 12.39103,6.66432 20.33599,7.49473 3.25359,0.34007 5.91561,0.92576 5.91561,1.30153 0,1.44445 -4.73676,5.03048 -9.51246,7.20153 -6.5395,2.9729 -18.4859,2.95917 -30.48754,-0.0351 -10.71473,-2.67315 -19.33613,-2.77216 -24.0156,-0.27579 -3.60756,1.92452 -8.5761,8.13069 -24.9844,31.2078 -17.32484,24.366172 -34.63757,39.564002 -55.71653,48.910302 -8.60616,3.81597 -24.86904,9.02312 -28.18071,9.02312 -1.05848,0 -2.813,-2.31079 -4.77418,-6.28785 -4.1414,-8.39833 -7.59602,-12.30724 -13.53799,-15.31826 -6.25499,-3.16965 -8.61817,-2.57268 -3.24446,0.8196 5.30637,3.34976 9.51613,8.24718 13.47688,15.6783 3.35193,6.28886 10.13186,13.28597 18.25295,18.8376 2.59822,1.77617 4.72404,3.61644 4.72404,4.08949 0,3.114 -13.05042,4.08687 -22.89455,1.70671 z m -72.69298,-55.13037 c 2.21835,-0.85126 0.94753,-2.39522 -1.97149,-2.39522 -2.5675,0 -3.32067,1.47837 -1.19098,2.33772 0.6875,0.27741 1.3257,0.53991 1.41821,0.58333 0.0925,0.0434 0.87743,-0.1932 1.74426,-0.52583 z m 4.58753,-3.19522 c 0,-2.78034 -8.97866,-4.66158 -10.5,-2.2 -0.36387,0.58876 0.74278,1.0063 2.69098,1.01531 1.81996,0.008 3.98402,0.45153 4.80902,0.98469 1.83718,1.18727 3,1.2648 3,0.2 z m 7.5,40.77676 c -20.49142,-3.15205 -43.06844,-10.90438 -64.500003,-22.14752 -11.9703,-6.2797 -29.6722,-17.52442 -31.9965,-20.32502 -1.21903,-1.46884 -0.8429,-1.55583 4.07679,-0.94286 4.26009,0.53079 6.59651,1.58365 10.91971,4.92077 26.875728,20.74564 63.732883,35.0798 95.477093,37.13211 6.90674,0.44653 10.90823,1.14512 11.64513,2.03303 1.63543,1.97057 -10.52066,1.65245 -25.62222,-0.67051 z m -27.085,-0.97287 C 89.788725,146.81433 52.465407,128.11804 38.324107,113.21346 l -4,-4.2159 4.75808,0.4999 c 3.11456,0.32723 5.8779,1.41189 8,3.14014 21.55823,17.55709 52.487258,32.85601 82.144663,40.63251 l 7.09726,1.86099 -5,-0.0292 c -2.75,-0.0161 -8.63825,-0.85161 -13.085,-1.85674 z m 26.4133,-4.04532 c -22.50993,-3.96533 -51.656535,-15.96472 -72.328303,-29.7769 l -10,-6.68167 5.83948,-0.30257 c 5.47888,-0.28389 6.15816,-0.0573 11,3.66876 21.849403,16.81448 53.424543,29.354 79.854513,31.71284 5.07182,0.45266 7.87279,1.15262 8.21862,2.05383 0.7561,1.97036 -9.27089,1.67099 -22.58431,-0.67429 z m 5.6717,-4.54964 C 126.75958,140.99146 97.687735,128.4645 79.824107,114.27193 l -2.5,-1.98624 5.89701,-0.0222 c 5.297268,-0.02 6.340128,0.34087 10.254098,3.54807 16.639015,13.63442 40.036515,23.29664 61.117725,25.23915 6.22918,0.57398 7.74579,1.07034 8.98466,2.94054 1.43134,2.16076 1.37654,2.24616 -1.38152,2.15314 -1.57958,-0.0533 -6.92197,-0.72566 -11.87197,-1.49421 z m -1,-6.12319 c -6.18518,-0.85478 -18.45181,-4.24227 -26.2455,-7.24784 -8.17282,-3.15177 -24.095095,-12.4588 -27.754505,-16.2233 l -2,-2.05744 5.5,0.59233 c 3.633945,0.39137 8.214265,1.99996 13.500005,4.74115 4.4,2.28184 10.925,5.67407 14.5,7.53827 6.37404,3.32377 21.38192,7.30667 27.6933,7.34945 2.93569,0.0199 6.3067,2.82296 6.3067,5.24415 0,0.78795 -6.01657,0.82103 -11.5,0.0632 z"
                  id="path1"
                />
              </g>
            </svg>
          </button>
          
          {/* Always-expanded Search */}
          <ExpandingSearch onResultClick={() => {}} />
        </div>
        
        {/* CENTER: Primary nav links */}
        <div className="nav-center">
          <button 
            className={`nav-link ${currentPage === 'home' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, '/home')}
            aria-current={currentPage === 'home' ? 'page' : undefined}
          >
            <Icon name="Home" className="icon--md" />
            <span>Home</span>
          </button>
          <button 
            className={`nav-link ${currentPage === 'insights' ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, '/insights')}
            aria-current={currentPage === 'insights' ? 'page' : undefined}
          >
            <Icon name="Lightbulb" className="icon--md" />
            <span>Insights</span>
          </button>
          {/* Primary CTA Button */}
          <button
            className="nav-link nav-link-cta"
            onClick={(e) => handleNavClick(e, '/protocols/create')}
            aria-label="Create a new protocol"
          >
            <Icon name="Plus" className="icon--md" />
            <span>Create Protocol</span>
          </button>
        </div>
        
        {/* RIGHT: Utilities */}
        <div className="nav-right">
          {/* Feedback Button */}
          <button
            className={`nav-icon nav-icon--feedback${currentPage === 'feedback' ? ' active' : ''}`}
            title="Feedback & Suggestions"
            aria-label="Submit feedback or suggestions"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsNotificationOpen(false);
              setShowUserSearch(false);
              
              // On mobile, always navigate to page (never open modal)
              if (window.innerWidth <= 480) {
                navigate('/feedback');
              } else {
                // On desktop, open modal
                setChecklistOpen(false);
                setIsFeedbackOpen(true);
              }
            }}
          >
            <Icon name="MessageCircleQuestionMark" className="icon--md" />
          </button>
          
          {/* Checklist Button */}
          <button 
            className="nav-icon" 
            title="Daily Checklist" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsNotificationOpen(false);
              setShowUserSearch(false);
              
              // On mobile, navigate to page; on desktop, open modal
              if (window.innerWidth <= 480) {
                navigate('/checklist');
              } else {
                setIsFeedbackOpen(false);
                setChecklistOpen(true);
              }
            }} 
            style={{ position: 'relative' }}
            aria-label={unloggedCount > 0 ? `Daily checklist with ${unloggedCount} pending items` : 'Daily checklist'}
          >
            <Icon name="CheckSquare" className="icon--md" />
            {unloggedCount > 0 && (
              <span className="nav-badge" aria-label={`${unloggedCount} unlogged hacks`}>
                {unloggedCount}
              </span>
            )}
          </button>
          
          {/* Notifications Button */}
          <button 
            className="nav-icon nav-icon--notifications" 
            title="Notifications" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowUserSearch(false);
              
              // On mobile, navigate to page; on desktop, toggle dropdown
              if (window.innerWidth <= 480) {
                navigate('/notifications');
              } else {
                setChecklistOpen(false);
                setIsFeedbackOpen(false);
                toggleNotifications(e);
              }
            }}
            style={{ position: 'relative' }}
            aria-label={notificationCount > 0 ? `Notifications - ${notificationCount} unread` : 'Notifications'}
            aria-expanded={isNotificationOpen}
          >
            <Icon name="Bell" className="icon--md" />
            {notificationCount > 0 && (
              <span className="nav-badge" aria-label={`${notificationCount} unread notifications`}>
                {notificationCount}
              </span>
            )}
          </button>
          
          {/* Messages Button */}
          <button
            className={`nav-icon nav-icon--messages${currentPage === 'messages' ? ' active' : ''}`}
            title="Messages"
            aria-label={unreadMessageCount > 0 ? `Messages - ${unreadMessageCount} unread` : 'Messages'}
            data-onboarding="messages"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsNotificationOpen(false);
              setShowUserSearch(false);
              
              // On desktop only, close modals (on mobile these are pages)
              if (window.innerWidth > 480) {
                setChecklistOpen(false);
                setIsFeedbackOpen(false);
              }
              
              navigate('/messages');
              fetchUnreadMessageCount();
            }}
            style={{ position: 'relative' }}
          >
            <Icon name="MessageCircle" className="icon--md" />
            {unreadMessageCount > 0 && (
              <span className="nav-badge" aria-label={`${unreadMessageCount} unread messages`}>
                {unreadMessageCount}
              </span>
            )}
          </button>
          
          {/* Profile Button */}
          <button
            className={`nav-icon nav-profile-btn${currentPage === 'user' ? ' active' : ''}`}
            aria-label="User profile"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsNotificationOpen(false);
              setShowUserSearch(false);
              
              // On desktop only, close modals (on mobile these are pages, not modals)
              if (window.innerWidth > 480) {
                setChecklistOpen(false);
                setIsFeedbackOpen(false);
              }
              
              // Navigate to user profile protocols page
              navigate('/user#protocols');
            }}
            title={userProfile?.username || 'User Profile'}
          >
            {userProfile?.avatar_url && userProfile.avatar_url !== '/default-avatar.jpg' ? (
              <img 
                src={userProfile.avatar_url} 
                alt={`${userProfile.username}'s profile`}
                className="nav-profile-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="nav-profile-fallback"
              style={{ 
                display: userProfile?.avatar_url && userProfile.avatar_url !== '/default-avatar.jpg' ? 'none' : 'flex' 
              }}
            >
              {(userProfile?.username || 'U').charAt(0).toUpperCase()}
            </div>
          </button>
        </div>
      </div>
      
      {/* ChecklistLogger panel - Desktop only (on mobile it's a full page) */}
      {isChecklistOpen && window.innerWidth > 480 && (
        <ChecklistLogger 
          open={isChecklistOpen} 
          onClose={handleChecklistClose} 
          onLogChange={refreshUnloggedCount} 
        />
      )}
      
      {/* NotificationDropdown */}
      {isNotificationOpen && (
        <NotificationDropdown 
          isOpen={isNotificationOpen} 
          onClose={() => setIsNotificationOpen(false)}
          onNotificationClick={() => {
            setIsNotificationOpen(false);
            fetchNotificationAndMessageCounts();
          }}
        />
      )}
      
      {/* Legacy User Search Modal */}
      <UserSearchModal open={showUserSearch} onClose={() => setShowUserSearch(false)} />
      
      {/* Feedback Box Modal - Desktop only (on mobile it's a full page) */}
      {window.innerWidth > 480 && (
        <FeedbackBox isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      )}
    </nav>
  );
};

export default TopNavigation;